import app from 'app';
import R from 'ramda';

import { logEvent, eventCategories } from 'helpers/analytics';

import './commPreferences.scss';
import commPreferencesTemplateUrl from './commPreferences.html';
import commPreferencesEditTemplateUrl from './commPreferencesEdit.html';

import './commPreferenceEnabledIcon/commPreferenceEnabledIcon';

// Modals.
import mxConfirmationModal from 'components/modals/mxConfirmationModal/mxConfirmationModal';
import mxExampleListModal from 'components/modals/mxExampleListModal/mxExampleListModal';

// Copy
import {
  COMM_SETTINGS,
  LEGALLY_REQUIRED_COMMS
} from 'constants/communicationSettings';

// Other components
import 'components/checkboxListItem/checkboxListItem';

import '@collectivehealth/dls-icons/dist/es/elements/dls-icon-statements';
import '@collectivehealth/dls-icons/dist/es/elements/dls-icon-envelope';
import '@collectivehealth/dls-icons/dist/es/elements/dls-icon-chevron-left';

function CommPreferencesCtrl($log, $q, animatedScrollTop, Api, chModal) {
  const Communication = this;

  Communication.commDictionary = {
    email: 'Email Only',
    postal_mail: 'Paper Only',
    email_and_postal: 'Emails and Paper'
  };

  Communication.sendDictionary = {
    true: 'Send',
    false: "Don't Send"
  };

  Communication.detailDictionary = {
    true: 'Enhanced',
    false: 'General'
  };

  Communication.commTypeOptions = [
    {
      label: 'Emails Only',
      subLabel: 'Send statements electronically. Save the trees!',
      value: 'email'
    },
    {
      label: 'Emails and paper',
      subLabel:
        'Send an email and a paper statement after every service received.',
      value: 'email_and_postal'
    },
    {
      label: 'Paper only',
      subLabel: 'Send only paper statements for every service received. ',
      value: 'postal_mail'
    }
  ];

  Communication.additionalEmailOptions = COMM_SETTINGS;
  Communication.legallyRequiredComms = LEGALLY_REQUIRED_COMMS;

  function parseSettingsResponse(response) {
    return R.pick(
      [
        'claimsEmailFrequency',
        'communication',
        'emailBenefitsUpdate',
        'emailNewOffers',
        'emailProgramPartnerUpdate',
        'emailResearchRequest',
        'enhancedClaimsEmail'
      ],
      response
    );
  }

  function showPaperOnlyConfirmation() {
    const deferred = $q.defer();

    chModal
      .open({
        component: mxConfirmationModal,
        locals: {
          header: 'Only receive paper statements?',
          body: `Emails from Collective Health are the easiest way
          to stay on top of your health information. We will never
          send you more than you request, you can adjust these
          settings at any time.`,
          confirm: 'Send email and paper',
          cancel: 'Only send paper'
        }
      })
      .on('close', sendEmailAndPostal => {
        deferred.resolve(
          sendEmailAndPostal ? 'email_and_postal' : 'postal_mail'
        );
      });

    return deferred.promise;
  }

  function checkForPaperOnlySave() {
    if (Communication.settings.communication === 'postal_mail') {
      // Display a modal and resolve with the user's choice when
      // it is closed.
      return showPaperOnlyConfirmation();
    } else {
      // Otherwise, just resolve with the current value.
      return $q.when(Communication.settings.communication);
    }
  }

  function getSettings() {
    Api.get(`/person/${Communication.currentUser.user.id}/setting`)
      .then(response => {
        Communication.settings = parseSettingsResponse(response);
      })
      .catch(err => {
        $log.log(err);
      });
  }

  function setSettings(settings) {
    Api.put(`/person/${Communication.currentUser.user.id}/setting`, settings);
  }

  function showToast(toastLevel, toastText) {
    Communication.showToast = true;
    Communication.toastLevel = toastLevel;
    Communication.toastText = toastText;
    animatedScrollTop();
  }

  Communication.$onInit = () => {
    getSettings();
  };

  Communication.showDetailedExample = () => {
    chModal.open({
      component: mxExampleListModal,
      locals: {
        title: 'Emails with health details',
        subtitle: [
          'No need to sign in—you’ll have details about doctor’s visits ',
          'prescriptions, and costs delivered right to your inbox.'
        ].join(''),
        list: [
          'Name of patient',
          'Number and date of claims or transactions',
          'Subject with general category',
          'Requests for documentation',
          'The name and sponsor of your plan',
          'Personalized health recommendations',
          'Costs or balances',
          'Doctor or merchant information',
          'Procedure or prescription name'
        ]
      }
    });
  };

  Communication.showGeneralExample = () => {
    chModal.open({
      component: mxExampleListModal,
      locals: {
        title: 'General emails (without health details)',
        subtitle:
          'You’ll need to sign in to see details about your care and costs.',
        list: [
          'Name of patient',
          'Number and date of claims or transactions',
          'Subject with general category',
          'Requests for documentation',
          'The name and sponsor of your plan'
        ]
      }
    });
  };

  Communication.reset = () => {
    getSettings(Communication.settings);
    Communication.settingsForm.$setPristine();
    logEvent('commsPreferencesReset', {
      category: eventCategories.SETTINGS
    });
  };

  Communication.save = () => {
    if (Communication.settingsForm.$valid) {
      checkForPaperOnlySave()
        .then(finalCommunicationPreference => {
          // Update the model and make the API request.
          Communication.settings.communication = finalCommunicationPreference;
          return setSettings(Communication.settings);
        })
        .then(() => {
          Communication.settingsForm.$setPristine();

          const includedTrackingOptions = [
            'emailBenefitsUpdate',
            'emailNewOffers',
            'emailProgramPartnerUpdate',
            'emailResearchRequest'
          ];

          const marketingOptions = Object.keys(Communication.settings).filter(
            key =>
              includedTrackingOptions.indexOf(key) > -1 &&
              Communication.settings[key]
          );

          logEvent('commsPreferencesEdited', {
            category: eventCategories.SETTINGS,
            statementDelivery: Communication.settings.communication,
            detailLevel: Communication.settings.enhancedClaimsEmail
              ? 'detailed'
              : 'general',
            marketingOptions
          });
          showToast(
            'success',
            'Your communication preferences were successfully updated!'
          );
        })
        .catch(() => {
          showToast(
            'error',
            'Hmm, we ran into a snag saving your communication preferences. Please try again.'
          );
        });
    }
  };
}

app.component('commPreferences', {
  bindings: {
    currentUser: '<'
  },
  controller: CommPreferencesCtrl,
  controllerAs: 'Communication',
  templateUrl: commPreferencesTemplateUrl
});

app.component('commPreferencesEdit', {
  bindings: {
    currentUser: '<'
  },
  controller: CommPreferencesCtrl,
  controllerAs: 'Communication',
  templateUrl: commPreferencesEditTemplateUrl
});

export const commPreferences = 'commPreferences';
export const commPreferencesEdit = 'commPreferencesEdit';
